import { NgIf, LowerCasePipe } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit, TemplateRef, ViewChild, AfterViewInit, Inject } from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import { MatTableDataSource } from '@angular/material/table';
import { MatTooltip } from '@angular/material/tooltip';
import { RootState } from '@next/core-lib/app';
import { AgrI18nSharedModule } from '@next/core-lib/i18n';
import { AgrPipesModule } from '@next/core-lib/pipes';
import { BreakpointService, BreakpointServiceInjector } from '@next/core-lib/responsive';
import { TableColumnTypes, TableConfig, AgrTableModule } from '@next/core-lib/table';
import { Store } from '@ngrx/store';
import { combineLatest, Observable, Subject } from 'rxjs';

import { takeUntil } from 'rxjs/operators';

import { Order, OrderColumnTypes, OrderStatus } from '../../models/order.models';
import { openOrderDetails, orderDetailsRequest } from '../../store/actions';

@Component({
  selector: 'app-orders-table',
  templateUrl: './orders-table.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatIcon, NgIf, AgrTableModule, MatTooltip, LowerCasePipe, AgrI18nSharedModule, AgrPipesModule],
})
export class OrdersTableComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('orderNumbersColumn') orderNumbersColumn: TemplateRef<unknown>;
  @ViewChild('orderTypeColumn') orderTypeColumn: TemplateRef<unknown>;
  @ViewChild('createdOnColumn') createdOnColumn: TemplateRef<unknown>;
  @ViewChild('orderNrOfProducts') orderNrOfProducts: TemplateRef<unknown>;
  @ViewChild('orderStatusColumn') orderStatusColumn: TemplateRef<unknown>;

  @Input() data$: Observable<Order[]>;
  @Input() showAllOrders: boolean;
  tableConfig: TableConfig;
  readonly destroy$ = new Subject<void>();

  readonly orderStatusType = OrderStatus;

  constructor(
    @Inject(BreakpointServiceInjector) readonly breakpoint: BreakpointService,
    private readonly store: Store<RootState>,
    private readonly changeDetectorRef: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.tableConfig = {
      dataSource: new MatTableDataSource([]),
      columns: [
        {
          id: OrderColumnTypes.createdOn,
          type: TableColumnTypes.TEMPLATE,
          label: `app.orders-table.columns.${OrderColumnTypes.createdOn}`,
          template: this.createdOnColumn,
        },
        {
          id: OrderColumnTypes.orderNumbers,
          type: TableColumnTypes.TEMPLATE,
          label: `app.orders-table.columns.${OrderColumnTypes.orderNumbers}`,
          template: this.orderNumbersColumn,
        },
        {
          id: OrderColumnTypes.orderType,
          type: TableColumnTypes.TEMPLATE,
          label: `app.orders-table.columns.${OrderColumnTypes.orderType}`,
          template: this.orderTypeColumn,
        },
        {
          id: OrderColumnTypes.webOrderNumber,
          type: TableColumnTypes.STRING,
          label: `app.orders-table.columns.${OrderColumnTypes.webOrderNumber}`,
        },
        {
          id: OrderColumnTypes.numberOfProducts,
          type: TableColumnTypes.TEMPLATE,
          label: `app.orders-table.columns.${OrderColumnTypes.numberOfProducts}`,
          template: this.orderNrOfProducts,
        },
        {
          id: OrderColumnTypes.orderStatus,
          type: TableColumnTypes.TEMPLATE,
          label: `app.orders-table.columns.${OrderColumnTypes.orderStatus}`,
          template: this.orderStatusColumn,
        },
      ],
      actionColumn: true,
      actions: [
        {
          id: 'details',
          label: 'app.orders.details',
          icon: 'info_outline',

          callback: this.openDetails.bind(this),
          disabled: () => false,
        },
      ],
      pagination: this.showAllOrders,
      primaryColumn: 'false',
      selectColumn: false,
      activeSort: {
        id: OrderColumnTypes.createdOn,
        start: 'desc',
        disableClear: false,
      },
      footer: false,
    };

    combineLatest([this.data$, this.breakpoint.ltSm$])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([orders, isMobile]) => {
        this.tableConfig = {
          ...this.tableConfig,
          dataSource: new MatTableDataSource(orders),
          primaryColumn: isMobile ? 'false' : OrderColumnTypes.orderNumbers,
        };
        this.changeDetectorRef.detectChanges();
      });
  }

  ngAfterViewInit(): void {
    this.tableConfig.columns[0].template = this.createdOnColumn;
    this.tableConfig.columns[1].template = this.orderNumbersColumn;
    this.tableConfig.columns[2].template = this.orderTypeColumn;
    this.tableConfig.columns[4].template = this.orderNrOfProducts;
    this.tableConfig.columns[5].template = this.orderStatusColumn;

    this.changeDetectorRef.detectChanges();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public openDetails({ id }: { id: number }): void {
    this.store.dispatch(orderDetailsRequest({ id }));
    this.store.dispatch(openOrderDetails());
  }
}
